import React, { useState } from 'react';
import {
  Box, Typography, Grid, Dialog, DialogTitle, DialogContent,
  TextField, Button, IconButton, Stack, CircularProgress,
  Checkbox, FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShieldMoonIcon from '@mui/icons-material/Shield';
import { usePaymentInputs } from 'react-payment-inputs';
import images from 'react-payment-inputs/images';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentService, AddCardPayload } from '../services/paymentService';

const EXTERNAL_LOGOS = {
  visa: "https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg",
  mastercard: "https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg",
  maestro: "https://upload.wikimedia.org/wikipedia/commons/f/fa/Maestro_Logo.svg",
  amex: "https://upload.wikimedia.org/wikipedia/commons/3/30/American_Express_logo.svg",
  jcb: "https://upload.wikimedia.org/wikipedia/commons/4/40/JCB_logo.svg",
  unionpay: "https://upload.wikimedia.org/wikipedia/commons/1/1b/UnionPay_logo.svg",
  discover: "https://upload.wikimedia.org/wikipedia/commons/5/57/Discover_Card_logo.svg",
};

const SECURITY_BADGE_LOGOS = {
  visaSecure: "https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg",
  mastercardId: "https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg",
  safekey: "https://www.americanexpress.com/content/dam/amex/us/merchant/safekey/SafeKey_Logo_300x120.png",
  jcbSecure: "https://www.global.jcb/en/common/img/brand/j-secure_logo.gif",
  pciDss: "https://www.pcisecuritystandards.org/images/pci_logo.png",
};

export interface AddCardModalProps {
  open: boolean;
  onClose: () => void;
}

const AddCardModal: React.FC<AddCardModalProps> = ({ open, onClose }) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvc, setCvc] = useState('');
  const [cardHolderName, setCardHolderName] = useState('');
  const [rememberCard, setRememberCard] = useState(true);

  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: (payload: AddCardPayload) => paymentService.addCard(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      onClose();
    },
  });

  const {
    getCardNumberProps,
    getExpiryDateProps,
    getCVCProps,
    getCardImageProps,
    meta,
  } = usePaymentInputs();

  const topLogos = [
    { name: 'visa', url: EXTERNAL_LOGOS.visa },
    { name: 'mastercard', url: EXTERNAL_LOGOS.mastercard },
    { name: 'maestro', url: EXTERNAL_LOGOS.maestro },
    { name: 'amex', url: EXTERNAL_LOGOS.amex },
    { name: 'jcb', url: EXTERNAL_LOGOS.jcb },
    { name: 'unionpay', url: EXTERNAL_LOGOS.unionpay },
    { name: 'discover', url: EXTERNAL_LOGOS.discover },
  ];

  const handleConfirm = () => {
    if (!meta.error && cardNumber && expiryDate && cvc && cardHolderName.trim()) {
      const cleanNumber = cardNumber.replace(/\s/g, '');
      const parts = expiryDate.replace(/\s/g, '').split('/');
      const expirationMonth = parts[0];
      const expirationYear = parts[1];

      addMutation.mutate({
        rememberCard,
        card: {
          number: cleanNumber,
          expirationMonth,
          expirationYear,
          securityCode: cvc,
          name: cardHolderName.trim(),
        },
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, px: 1 } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.2rem', pt: 4 }}>
        Add a new card
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12, color: '#999' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 4 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 3, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
          {topLogos.map((logo) => (
            <Box
              key={logo.name}
              component="img"
              src={logo.url}
              alt={logo.name}
              sx={{
                width: 40,
                height: 26,
                border: '1px solid #eee',
                borderRadius: '4px',
                p: '3px',
                objectFit: 'contain',
                bgcolor: 'white',
              }}
            />
          ))}
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Card No. *</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Card number"
              error={Boolean(meta.erroredInputs.cardNumber)}
              helperText={meta.erroredInputs.cardNumber}
              inputProps={{
                ...getCardNumberProps({ onChange: (e: any) => setCardNumber(e.target.value) }),
              }}
              InputProps={{
                endAdornment: (
                  <Box sx={{ width: 30, display: 'flex', alignItems: 'center' }}>
                    <svg {...getCardImageProps({ images })} />
                  </Box>
                ),
              }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Name on card *</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Cardholder name"
              value={cardHolderName}
              onChange={(e) => setCardHolderName(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Expiration date *</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="MM/YY"
              error={Boolean(meta.erroredInputs.expiryDate)}
              helperText={meta.erroredInputs.expiryDate}
              inputProps={{
                ...getExpiryDateProps({ onChange: (e: any) => setExpiryDate(e.target.value) }),
              }}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Security code *</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="CVC"
              error={Boolean(meta.erroredInputs.cvc)}
              helperText={meta.erroredInputs.cvc}
              inputProps={{
                ...getCVCProps({ onChange: (e: any) => setCvc(e.target.value) }),
              }}
            />
            <Typography variant="caption" sx={{ color: '#666', cursor: 'pointer', textDecoration: 'underline', mt: 0.5, display: 'block' }}>
              What is a security code?
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberCard}
                  onChange={(e) => setRememberCard(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">Remember this card</Typography>}
            />
          </Grid>
        </Grid>

        <Box sx={{ my: 3, borderTop: '1px solid #eee', pt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ color: '#1b5e20', display: 'flex', alignItems: 'center' }}>
              <ShieldMoonIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="subtitle2" sx={{ color: '#1b5e20', fontWeight: 'bold' }}>
              Payment Security
            </Typography>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, lineHeight: 1.3 }}>
            SHEIN is committed to protecting your payment information and only shares your credit card information with our payment service providers who agreed to safeguard your information.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
            {Object.entries(SECURITY_BADGE_LOGOS).map(([key, url]) => (
              <Box
                key={key}
                component="img"
                src={url}
                alt={key}
                sx={{
                  height: 22,
                  width: 'auto',
                  filter: 'grayscale(0.2)',
                  opacity: 0.8,
                  objectFit: 'contain',
                }}
              />
            ))}
          </Box>
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleConfirm}
          disabled={addMutation.isPending}
          sx={{ bgcolor: 'black', color: 'white', borderRadius: 0, py: 1.5, fontWeight: 'bold' }}
        >
          {addMutation.isPending ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'CONFIRM'}
        </Button>
        {addMutation.isError && (
          <Typography color="error" variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            Failed to add card. Please try again.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddCardModal;
